import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import SectionHeading from "./SectionHeading";
import { socials } from "../constants/data";

const Contact = () => {
    const formRef = useRef();
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [status, setStatus] = useState("idle"); // idle | sending | sent | error

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setStatus("sending");

        emailjs
            .sendForm(
                "service_c184d2j",
                "template_kxjfptz",
                formRef.current,
                "ArsedafXN6JRj_MaZ"
            )
            .then(
                () => {
                    setStatus("sent");
                    setForm({ name: "", email: "", message: "" });
                    setTimeout(() => setStatus("idle"), 6000);
                },
                (error) => {
                    console.error("EmailJS error:", error);
                    setStatus("error");
                    setTimeout(() => setStatus("idle"), 6000);
                }
            );
    };

    const inputClass =
        "w-full border-b border-line bg-transparent py-3 font-mono text-sm text-fg placeholder-faint transition-colors focus:border-accent focus:outline-none";

    return (
        <section id="contact" className="py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHeading
                    index="04"
                    label="Say Hello"
                    title={
                        <>
                            Have a hard problem?{" "}
                            <span className="font-serif italic text-accent">
                                Let's talk.
                            </span>
                        </>
                    }
                />

                <div className="grid gap-16 lg:grid-cols-[1fr_1.3fr]">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, margin: "-80px" }}
                    >
                        <p className="mb-10 max-w-md leading-relaxed text-muted">
                            Whether it's an AI product that needs to actually work in
                            production, or a full-stack system that needs a senior hand
                            — my inbox is open.
                        </p>

                        <dl className="space-y-6 font-mono text-sm">
                            <div>
                                <dt className="mb-1 text-[11px] uppercase tracking-widest text-faint">
                                    Email
                                </dt>
                                <dd>
                                    <a
                                        href={`mailto:${socials.email}`}
                                        className="link-sweep pb-0.5 text-fg hover:text-accent"
                                    >
                                        {socials.email}
                                    </a>
                                </dd>
                            </div>
                            <div>
                                <dt className="mb-1 text-[11px] uppercase tracking-widest text-faint">
                                    Phone
                                </dt>
                                <dd>
                                    <a
                                        href={`tel:${socials.phone.replace(/\s/g, "")}`}
                                        className="link-sweep pb-0.5 text-fg hover:text-accent"
                                    >
                                        {socials.phone}
                                    </a>
                                </dd>
                            </div>
                            <div>
                                <dt className="mb-1 text-[11px] uppercase tracking-widest text-faint">
                                    Base
                                </dt>
                                <dd className="text-fg">{socials.location}</dd>
                            </div>
                            <div>
                                <dt className="mb-1 text-[11px] uppercase tracking-widest text-faint">
                                    Elsewhere
                                </dt>
                                <dd className="flex gap-5">
                                    <a
                                        href={socials.github}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-fg transition-colors hover:text-accent"
                                    >
                                        GitHub ↗
                                    </a>
                                    <a
                                        href={socials.linkedin}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-fg transition-colors hover:text-accent"
                                    >
                                        LinkedIn ↗
                                    </a>
                                </dd>
                            </div>
                        </dl>
                    </motion.div>

                    <motion.form
                        ref={formRef}
                        onSubmit={handleSubmit}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        className="rounded-2xl border border-line bg-elevated p-8 shadow-soft sm:p-10"
                    >
                        <p className="mb-8 font-mono text-xs uppercase tracking-widest text-faint">
                            <span className="text-accent">{"//"}</span> leave a message
                        </p>

                        <div className="mb-8 grid gap-8 sm:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="name"
                                    className="mb-2 block font-mono text-[11px] uppercase tracking-widest text-muted"
                                >
                                    --name
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Ada Lovelace"
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="email"
                                    className="mb-2 block font-mono text-[11px] uppercase tracking-widest text-muted"
                                >
                                    --reply-to
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="you@company.com"
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <div className="mb-10">
                            <label
                                htmlFor="message"
                                className="mb-2 block font-mono text-[11px] uppercase tracking-widest text-muted"
                            >
                                --message
                            </label>
                            <textarea
                                name="message"
                                id="message"
                                rows="5"
                                value={form.message}
                                onChange={handleChange}
                                required
                                placeholder="Tell me what you're building and where it hurts…"
                                className={`${inputClass} resize-none`}
                            />
                        </div>

                        <div className="flex flex-wrap items-center gap-5">
                            <button
                                type="submit"
                                disabled={status === "sending"}
                                className="rounded-full bg-fg px-7 py-3 font-mono text-sm uppercase tracking-widest text-base transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {status === "sending" ? "Sending…" : "Send Message →"}
                            </button>
                            {status === "sent" && (
                                <p className="font-mono text-xs text-accent">
                                    ✓ message delivered — exit code 0
                                </p>
                            )}
                            {status === "error" && (
                                <p className="font-mono text-xs text-pop">
                                    ✗ sending failed — try email instead
                                </p>
                            )}
                        </div>
                    </motion.form>
                </div>
            </div>
        </section>
    );
};

export default Contact;